import { css } from '@emotion/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { FieldFactory } from '../../../components/Form/FieldFactory';
import { FormLabelText } from '../../../components/Form/FormComponents';
import { RendererType } from '../../../data-models/field-definition';
import { FilterableColumn, FilterType, FilterValue } from '../../../schemas/DownloadConfig.schema';
import { dateToPlainDateString, isDateOrDateString, nextPlainDateString } from '../../../util/date-utilts';
import { createFormField } from '../../../view-models/form.view-model';

const FilterContainerCss = css`
  display: grid;
  grid-template-columns: 15rem 2fr;
  align-items: center;
  column-gap: 1rem;
  & .MuiFormControl-root {
    margin: 0 !important;
  }
`;
interface IFilterBuilderProps {
  columns: FilterableColumn[];
  filters: Record<string, string | number | boolean | FilterValue>;
  onFilterChange: (columnName: string, value: string | number | boolean | FilterValue | null) => void;
}

export function FilterBuilder({ columns, filters, onFilterChange }: IFilterBuilderProps) {
  return (
    <Stack spacing={1}>
      {columns.map((column) => (
        <FilterField
          key={column.columnName}
          column={column}
          value={filters[column.columnName]}
          onChange={(value) => onFilterChange(column.columnName, value)}
        />
      ))}
    </Stack>
  );
}

interface IFilterFieldProps {
  column: FilterableColumn;
  value: string | number | boolean | FilterValue | undefined;
  onChange: (value: string | number | boolean | FilterValue | null) => void;
}

function FilterField({ column, value, onChange }: IFilterFieldProps) {
  const [operator, setOperator] = useState<string>('eq');
  const [filterValue, setFilterValue] = useState<string | number | boolean>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const handleOperatorChange = (newOperator: string) => {
    setOperator(newOperator);
    if (filterValue !== '' && filterValue !== null && filterValue !== undefined) {
      applyFilter(newOperator, filterValue);
    }
  };

  const handleValueChange = (newValue: string | number | boolean) => {
    setFilterValue(newValue);
    if (newValue !== '' && newValue !== null && newValue !== undefined) {
      applyFilter(operator, newValue);
    } else {
      onChange(null);
    }
  };

  const applyFilter = (op: string, val: string | number | boolean) => {
    // A picked enum value is exact, so send `eq`: a plain value on a server-side string column means "contains".
    if (op === 'eq' && column.filterType !== FilterType.ENUM) {
      onChange(val);
    } else {
      onChange({ [op]: val } as FilterValue);
    }
  };

  const applyDateRange = (from: string, to: string) => {
    if (!from && !to) {
      onChange(null);
      return;
    }
    const next: FilterValue = {};
    if (from) {
      next.gte = from;
    }
    if (to) {
      // lt (not lte) so timestamp columns include rows later on the To day, not just midnight.
      next.lt = nextPlainDateString(to);
    }
    onChange(next);
  };

  const handleFromDateChange = (newValue: Date | null) => {
    if (newValue !== null && !isDateOrDateString(newValue, RendererType.date)) {
      return;
    }
    const plain = newValue ? dateToPlainDateString(newValue) : '';
    setFromDate(plain);
    applyDateRange(plain, toDate);
  };

  const handleToDateChange = (newValue: Date | null) => {
    if (newValue !== null && !isDateOrDateString(newValue, RendererType.date)) {
      return;
    }
    const plain = newValue ? dateToPlainDateString(newValue) : '';
    setToDate(plain);
    applyDateRange(fromDate, plain);
  };

  const renderFilterInput = () => {
    const baseValueField = createFormField({
      key: 'value',
      label: 'Value',
      renderer: RendererType.text,
      placeholder: `Enter ${column.displayName.toLowerCase()}`,
    });
    const valueFieldProps = {
      value: filterValue,
      onChange: (event: ChangeEvent<HTMLInputElement>) => handleValueChange(event.target.value),
    } as ControllerRenderProps;

    const operatorFieldProps = {
      value: operator,
      onChange: handleOperatorChange,
    } as ControllerRenderProps;

    switch (column.filterType) {
      case FilterType.STRING: {
        const operatorField = createFormField({
          key: 'operator',
          label: 'Operator',
          renderer: RendererType.singleSelect,
          rendererMeta: {
            values: [
              { value: 'eq', displayName: 'Equals' },
              { value: 'neq', displayName: 'Not Equals' },
              { value: 'like', displayName: 'Contains' },
            ],
          },
        });
        const valueField = { ...baseValueField, label: column.displayName };
        return (
          <Stack css={FilterContainerCss}>
            <Stack>
              <FormLabelText label={operatorField.label!} />
              <FieldFactory formField={operatorField} formProps={operatorFieldProps} />
            </Stack>
            <Stack>
              <FormLabelText label={valueField.label!} />
              <FieldFactory formField={valueField} formProps={valueFieldProps} />
            </Stack>
          </Stack>
        );
      }

      case FilterType.NUMBER: {
        const operatorField = createFormField({
          key: 'operator',
          label: 'Operator',
          renderer: RendererType.singleSelect,
          rendererMeta: {
            values: [
              { value: 'eq', displayName: 'Equals' },
              { value: 'neq', displayName: 'Not Equals' },
              { value: 'gt', displayName: 'Greater Than' },
              { value: 'gte', displayName: 'Greater or Equal' },
              { value: 'lt', displayName: 'Less Than' },
              { value: 'lte', displayName: 'Less or Equal' },
            ],
          },
        });

        const valueField = { ...baseValueField, renderer: RendererType.number, label: column.displayName };
        const numberValueFieldProps = {
          ...valueFieldProps,
          onChange: (newValue: number) => handleValueChange(newValue),
        };

        return (
          <Stack css={FilterContainerCss}>
            <Stack>
              <FormLabelText label={operatorField.label!} />
              <FieldFactory formField={operatorField} formProps={operatorFieldProps} />
            </Stack>
            <Stack>
              <FormLabelText label={valueField.label!} />
              <FieldFactory formField={valueField} formProps={numberValueFieldProps} />
            </Stack>
          </Stack>
        );
      }
      case FilterType.DATE: {
        const fromField = createFormField({
          key: 'from',
          label: 'From',
          renderer: RendererType.date,
        });
        const toField = createFormField({
          key: 'to',
          label: 'To',
          renderer: RendererType.date,
        });
        const fromFieldProps = {
          value: fromDate,
          onChange: handleFromDateChange,
        } as ControllerRenderProps;
        const toFieldProps = {
          value: toDate,
          onChange: handleToDateChange,
        } as ControllerRenderProps;

        // Plain YYYY-MM-DD strings compare chronologically.
        const isReversedRange = fromDate !== '' && toDate !== '' && fromDate > toDate;

        return (
          <Stack css={FilterContainerCss}>
            <Stack>
              <FormLabelText label={fromField.label!} />
              <FieldFactory formField={fromField} formProps={fromFieldProps} />
            </Stack>
            <Stack>
              <FormLabelText label={toField.label!} />
              <FieldFactory formField={toField} formProps={toFieldProps} />
            </Stack>
            {isReversedRange && (
              <Typography role='alert' color='error' variant='caption' gridColumn='1 / -1'>
                From must be on or before To. This range matches no rows.
              </Typography>
            )}
          </Stack>
        );
      }

      case FilterType.BOOLEAN: {
        const operatorField = createFormField({
          key: 'operator',
          label: 'Operator',
          renderer: RendererType.singleSelect,
          rendererMeta: {
            values: [
              { value: 'eq', displayName: 'Equals' },
              { value: 'neq', displayName: 'Not Equals' },
            ],
          },
        });

        const valueField = { ...baseValueField, renderer: RendererType.boolean, label: column.displayName };

        const booleanValueFieldProps = {
          ...valueFieldProps,
          onChange: (newValue: boolean) => handleValueChange(newValue),
        };

        return (
          <Stack css={FilterContainerCss}>
            <Stack>
              <FormLabelText label={operatorField.label!} />
              <FieldFactory formField={operatorField} formProps={operatorFieldProps} />
            </Stack>
            <Stack>
              <FormLabelText label={valueField.label!} />
              <FieldFactory formField={valueField} formProps={booleanValueFieldProps} />
            </Stack>
          </Stack>
        );
      }

      case FilterType.ENUM: {
        const operatorField = createFormField({
          key: 'operator',
          label: 'Operator',
          renderer: RendererType.singleSelect,
          rendererMeta: {
            values: [
              { value: 'eq', displayName: 'Equals' },
              { value: 'neq', displayName: 'Not Equals' },
            ],
          },
        });

        // FieldSelect hands onChange the picked value itself, not a change event.
        const enumValueFieldProps = {
          value: filterValue,
          onChange: (newValue: string | null) => handleValueChange(newValue ?? ''),
        } as ControllerRenderProps;

        const valueField = {
          ...baseValueField,
          renderer: RendererType.singleSelect,
          label: column.displayName,
          rendererMeta: {
            values: column.enumValues?.map((value) => ({ value, displayName: value })),
          },
        };

        return (
          <Stack css={FilterContainerCss}>
            <Stack>
              <FormLabelText label={operatorField.label!} />
              <FieldFactory formField={operatorField} formProps={operatorFieldProps} />
            </Stack>
            <Stack>
              <FormLabelText label={valueField.label!} />
              <FieldFactory formField={valueField} formProps={enumValueFieldProps} />
            </Stack>
          </Stack>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>
          {column.displayName}
          {value && (
            <Typography component='span' color='primary' sx={{ ml: 1 }}>
              (filtered)
            </Typography>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box>{renderFilterInput()}</Box>
      </AccordionDetails>
    </Accordion>
  );
}
